import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TransactionHistory = ({ transactions, selectedCategory, onCategoryChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('all');

  const categories = ['all', 'Shopping', 'Food & Dining', 'Transportation', 'Entertainment', 'Bills & Utilities', 'Other'];
  const paymentMethods = ['all', 'BTC', 'ETH', 'USDC', 'Card'];

  const filteredTransactions = transactions?.filter(tx => {
    const matchesSearch = tx?.merchant?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tx?.category === selectedCategory;
    const matchesPayment = selectedPaymentMethod === 'all' || tx?.paymentMethod === selectedPaymentMethod;
    return matchesSearch && matchesCategory && matchesPayment;
  });

  const getPaymentMethodColor = (method) => {
    switch (method) {
      case 'BTC': return 'text-warning';
      case 'ETH': return 'text-primary';
      case 'USDC': return 'text-success';
      case 'Card': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Transaction History</h2>
          <p className="card-description">{filteredTransactions?.length} transactions found</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth flex items-center gap-2">
            <Icon name="Download" size={16} />
            Export
          </button>
          <button className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-smooth flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Transaction
          </button>
        </div>
      </div>
      <div className="card-content">
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap">
              {categories?.map(cat => (
                <button
                  key={cat}
                  onClick={() => onCategoryChange(cat)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
                    selectedCategory === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="w-px bg-border" />
            <div className="flex gap-2 flex-wrap">
              {paymentMethods?.map(method => (
                <button
                  key={method}
                  onClick={() => setSelectedPaymentMethod(method)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
                    selectedPaymentMethod === method
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {filteredTransactions?.map((tx) => (
            <div 
              key={tx?.id}
              className="flex items-center gap-4 p-4 bg-muted/30 hover:bg-muted/50 rounded-lg transition-smooth cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={tx?.icon} size={20} className="text-primary" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-foreground">{tx?.merchant}</p>
                  {tx?.receipt && (
                    <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{tx?.category}</span>
                  <span>•</span>
                  <span>{tx?.date} at {tx?.time}</span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className={`text-lg font-bold ${
                    tx?.amount < 0 ? 'text-error' : 'text-success'
                  }`}>
                    {tx?.amount < 0 ? '-' : '+'}${Math.abs(tx?.amount)?.toFixed(2)}
                  </p>
                  <p className={`text-xs font-medium ${getPaymentMethodColor(tx?.paymentMethod)}`}>
                    {tx?.paymentMethod}
                  </p>
                </div>

                <div className="opacity-0 group-hover:opacity-100 transition-smooth">
                  <button className="p-2 hover:bg-muted rounded-lg transition-smooth">
                    <Icon name="MoreVertical" size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTransactions?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-semibold text-foreground mb-2">No transactions found</p>
            <p className="text-sm text-muted-foreground">Try adjusting your filters or search query</p>
          </div>
        )}

        {filteredTransactions?.length > 0 && (
          <div className="mt-6 text-center">
            <button className="px-6 py-2.5 bg-muted/50 hover:bg-muted rounded-lg text-sm font-medium text-foreground transition-smooth">
              Load More Transactions
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;